import type { ColumnDefinition } from './types'
import type { TablePreferencesV1 } from './types'
import { TABLE_PREFERENCES_VERSION } from './types'
import { getInitialColumnVisibility } from './columnAdapter'

// ─── Consolidated storage (v3) ───────────────────────────────────────────────
// One browser-level key for all list-view state:
//   lion-pro.listview-prefs:v3
//   → { [userId]: { [listId]: { view?, prefs? } } }
//
// v2 used one key per user (`…:v2:{userId}`), which littered localStorage when
// pages passed `anonymous` or when multiple accounts were used. v3 folds
// everything into a single document and skips non-persistable user ids.

const STORAGE_KEY_V3 = 'lion-pro.listview-prefs:v3'
const CONSOLIDATED_KEY_PREFIX_V2 = 'lion-pro.listview-prefs:v2'
const LEGACY_PREFS_PREFIX = 'lion-pro.table-prefs:v1'
const LEGACY_VIEW_PREFIX = 'lion-pro.listview-view'
const CLEANUP_DONE_KEY_V2 = 'lion-pro.listview-cleanup-v2'

interface ConsolidatedEntry {
  view?: string
  prefs?: Partial<TablePreferencesV1>
  /** Per-listId selected saved view (null = none). */
  savedViews?: {
    selectedId?: string | null
    /** Only used on reserved `__svrt__:{resourceType}` keys. */
    includedSharedIds?: string[]
  }
}

/** Reserved listId prefix for per-resourceType saved-view opt-ins. */
const SAVED_VIEW_RESOURCE_META_PREFIX = '__svrt__:'

function resourceMetaListId(resourceType: string): string {
  return `${SAVED_VIEW_RESOURCE_META_PREFIX}${resourceType}`
}

function isResourceMetaListId(listId: string): boolean {
  return listId.startsWith(SAVED_VIEW_RESOURCE_META_PREFIX)
}

/** Per-user map of listId → prefs/view. */
type UserListStore = Record<string, ConsolidatedEntry>
/** Browser-level map of userId → list stores. */
type BrowserStore = Record<string, UserListStore>

function isPersistableUserId(userId: string | null | undefined): userId is string {
  if (!userId) return false
  const id = userId.trim()
  if (!id) return false
  if (id === 'anonymous' || id === 'null' || id === 'undefined') return false
  return true
}

function isEntryEmpty(entry: ConsolidatedEntry | undefined): boolean {
  if (!entry) return true
  const hasView = typeof entry.view === 'string' && entry.view.length > 0
  const hasPrefs = Boolean(entry.prefs) && Object.keys(entry.prefs!).length > 0
  const hasSelected =
    entry.savedViews?.selectedId != null && entry.savedViews.selectedId !== ''
  const hasIncluded =
    Array.isArray(entry.savedViews?.includedSharedIds) &&
    entry.savedViews!.includedSharedIds!.length > 0
  return !hasView && !hasPrefs && !hasSelected && !hasIncluded
}

function pruneUserStore(store: UserListStore): UserListStore {
  const next: UserListStore = {}
  for (const [listId, entry] of Object.entries(store)) {
    if (!listId || isEntryEmpty(entry)) continue
    next[listId] = entry
  }
  return next
}

function pruneBrowserStore(store: BrowserStore): BrowserStore {
  const next: BrowserStore = {}
  for (const [userId, userStore] of Object.entries(store)) {
    if (!isPersistableUserId(userId)) continue
    const pruned = pruneUserStore(userStore)
    if (Object.keys(pruned).length > 0) next[userId] = pruned
  }
  return next
}

function snapshotLocalStorageKeys(): string[] {
  const allKeys: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i)
    if (k) allKeys.push(k)
  }
  return allKeys
}

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}

/** Fold v1 per-list keys + v2 per-user keys into a single v3 browser store. */
function migrateToV3(): BrowserStore {
  const store: BrowserStore = {}

  const ensureUser = (userId: string): UserListStore => {
    if (!store[userId]) store[userId] = {}
    return store[userId]
  }

  const allKeys = snapshotLocalStorageKeys()

  for (const key of allKeys) {
    // v2: lion-pro.listview-prefs:v2:{userId}
    if (key.startsWith(`${CONSOLIDATED_KEY_PREFIX_V2}:`)) {
      const userId = key.slice(`${CONSOLIDATED_KEY_PREFIX_V2}:`.length)
      if (!isPersistableUserId(userId)) continue
      const parsed = readJson<UserListStore>(key)
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) continue
      const userStore = ensureUser(userId)
      for (const [listId, entry] of Object.entries(parsed)) {
        if (!listId || !entry || typeof entry !== 'object') continue
        userStore[listId] = { ...userStore[listId], ...entry }
      }
      continue
    }

    // v1 prefs: lion-pro.table-prefs:v1:{userId}:{tableId}
    if (key.startsWith(`${LEGACY_PREFS_PREFIX}:`)) {
      const rest = key.slice(`${LEGACY_PREFS_PREFIX}:`.length)
      const sep = rest.indexOf(':')
      if (sep === -1) continue
      const userId = rest.slice(0, sep)
      const tableId = rest.slice(sep + 1)
      if (!isPersistableUserId(userId) || !tableId) continue
      const prefs = readJson<Partial<TablePreferencesV1>>(key)
      if (!prefs) continue
      const userStore = ensureUser(userId)
      userStore[tableId] = { ...userStore[tableId], prefs }
      continue
    }

    // v1 view: lion-pro.listview-view:{userId}:{tableId}
    if (key.startsWith(`${LEGACY_VIEW_PREFIX}:`)) {
      const rest = key.slice(`${LEGACY_VIEW_PREFIX}:`.length)
      const sep = rest.indexOf(':')
      if (sep === -1) continue
      const userId = rest.slice(0, sep)
      const tableId = rest.slice(sep + 1)
      if (!isPersistableUserId(userId) || !tableId) continue
      try {
        const view = localStorage.getItem(key)
        if (!view) continue
        const userStore = ensureUser(userId)
        userStore[tableId] = { ...userStore[tableId], view }
      } catch { /* skip */ }
    }
  }

  return pruneBrowserStore(store)
}

function isLegacyOrV2Key(key: string): boolean {
  return (
    key.startsWith(`${CONSOLIDATED_KEY_PREFIX_V2}:`) ||
    key.startsWith(`${LEGACY_PREFS_PREFIX}:`) ||
    key.startsWith(`${LEGACY_VIEW_PREFIX}:`) ||
    key === CLEANUP_DONE_KEY_V2 ||
    key === 'lion-pro.listview-cleanup-v3' ||
    /^table-[a-z]/.test(key)
  )
}

function hasLegacyOrV2Keys(): boolean {
  return snapshotLocalStorageKeys().some(isLegacyOrV2Key)
}

function removeLegacyAndV2Keys(): void {
  for (const key of snapshotLocalStorageKeys()) {
    if (!isLegacyOrV2Key(key)) continue
    try {
      localStorage.removeItem(key)
    } catch { /* best-effort */ }
  }
}

function runV3Migration(): BrowserStore {
  if (typeof window === 'undefined') return {}
  try {
    const existing = readJson<BrowserStore>(STORAGE_KEY_V3)
    if (existing && typeof existing === 'object' && !Array.isArray(existing)) {
      const pruned = pruneBrowserStore(existing)
      // Fold any leftover legacy/v2 keys, then delete them so localStorage stays tidy.
      if (hasLegacyOrV2Keys()) {
        const fromLegacy = migrateToV3()
        for (const [userId, userStore] of Object.entries(fromLegacy)) {
          pruned[userId] = { ...(pruned[userId] ?? {}), ...userStore }
        }
        const merged = pruneBrowserStore(pruned)
        saveBrowserStore(merged)
        removeLegacyAndV2Keys()
        return merged
      }
      // Persist prune if anonymous / empty entries were stripped.
      if (JSON.stringify(pruned) !== JSON.stringify(existing)) {
        saveBrowserStore(pruned)
      }
      return pruned
    }

    const migrated = migrateToV3()
    saveBrowserStore(migrated)
    removeLegacyAndV2Keys()
    return migrated
  } catch {
    return {}
  }
}

function loadBrowserStore(): BrowserStore {
  if (typeof window === 'undefined') return {}
  try {
    return runV3Migration()
  } catch {
    return {}
  }
}

function saveBrowserStore(store: BrowserStore): void {
  if (typeof window === 'undefined') return
  try {
    const pruned = pruneBrowserStore(store)
    if (Object.keys(pruned).length === 0) {
      localStorage.removeItem(STORAGE_KEY_V3)
      return
    }
    localStorage.setItem(STORAGE_KEY_V3, JSON.stringify(pruned))
  } catch (e) {
    console.error('Failed to save listview prefs', e)
  }
}

// Module-level cache so multiple hooks don't each re-read localStorage on mount.
let browserStoreCache: BrowserStore | null = null

function getBrowserStore(): BrowserStore {
  if (!browserStoreCache) {
    browserStoreCache = loadBrowserStore()
  }
  return browserStoreCache
}

function mutateUserStore(
  userId: string,
  updater: (s: UserListStore) => UserListStore,
): void {
  if (!isPersistableUserId(userId)) return
  const browser = { ...getBrowserStore() }
  const current = browser[userId] ?? {}
  const nextUser = pruneUserStore(updater(current))
  if (Object.keys(nextUser).length === 0) delete browser[userId]
  else browser[userId] = nextUser
  browserStoreCache = pruneBrowserStore(browser)
  saveBrowserStore(browserStoreCache)
}

// ─── Public API ──────────────────────────────────────────────────────────────

export function loadViewPreference(userId: string, listId: string): string | null {
  if (!isPersistableUserId(userId) || !listId) return null
  return getBrowserStore()[userId]?.[listId]?.view ?? null
}

export function saveViewPreference(userId: string, listId: string, view: string): void {
  if (!isPersistableUserId(userId) || !listId) return
  mutateUserStore(userId, (s) => ({
    ...s,
    [listId]: { ...s[listId], view },
  }))
}

/** @deprecated use consolidated storage — kept only so nothing else breaks */
export function tablePreferencesKey(userId: string, tableId: string): string {
  return `${LEGACY_PREFS_PREFIX}:${userId}:${tableId}`
}

export function getDefaultColumnSizing<TRow>(
  columns: ColumnDefinition<TRow>[],
): Record<string, number> {
  const out: Record<string, number> = {}
  for (const c of columns) {
    if (c.width != null) {
      out[c.id] = c.width
    } else if (c.minWidth != null) {
      out[c.id] = c.minWidth
    }
  }
  return out
}

export function getDefaultVisibleFilterIds<TRow>(
  columns: ColumnDefinition<TRow>[],
): string[] {
  return columns
    .filter((c) => c.filterType && c.showFilterByDefault !== false)
    .map((c) => c.id)
}

function applyPinnedLeft<TRow>(order: string[], columns: ColumnDefinition<TRow>[]): string[] {
  const pinnedIds = columns.filter((c) => c.pinnedLeft).map((c) => c.id)
  if (pinnedIds.length === 0) return order
  const pinnedSet = new Set(pinnedIds)
  return [...pinnedIds, ...order.filter((id) => !pinnedSet.has(id))]
}

export function getDefaultTablePreferences<TRow>(
  columns: ColumnDefinition<TRow>[],
  overrides?: Partial<Omit<TablePreferencesV1, 'version'>>,
): TablePreferencesV1 {
  return {
    version: TABLE_PREFERENCES_VERSION,
    pagination: { pageIndex: 0, pageSize: 25 },
    columnFilters: [],
    columnVisibility: getInitialColumnVisibility(columns),
    columnOrder: applyPinnedLeft(columns.map((c) => c.id), columns),
    visibleFilterIds: getDefaultVisibleFilterIds(columns),
    ...overrides,
  }
}

function validateColumnOrder(
  order: string[] | undefined,
  validIds: Set<string>,
  defaultOrder: string[],
): string[] {
  if (!order?.length) return defaultOrder
  const valid = order.filter((id) => validIds.has(id))
  const missing = defaultOrder.filter((id) => !valid.includes(id))
  return [...valid, ...missing]
}

function filterRecord<T>(
  obj: Record<string, T> | undefined,
  validIds: Set<string>,
): Record<string, T> {
  const out: Record<string, T> = {}
  if (!obj) return out
  for (const [id, val] of Object.entries(obj)) {
    if (validIds.has(id)) out[id] = val
  }
  return out
}

export function mergeTablePreferences<TRow>(
  columns: ColumnDefinition<TRow>[],
  loaded: Partial<TablePreferencesV1> | null,
  defaults?: Partial<Omit<TablePreferencesV1, 'version'>>,
): TablePreferencesV1 {
  const base = getDefaultTablePreferences(columns, defaults)
  if (!loaded) return base

  const validIds = new Set(columns.map((c) => c.id))

  return {
    version: TABLE_PREFERENCES_VERSION,
    pagination: loaded.pagination ?? base.pagination,
    columnFilters: loaded.columnFilters ?? base.columnFilters,
    sort: loaded.sort ?? base.sort,
    columnVisibility: {
      ...base.columnVisibility,
      ...filterRecord(loaded.columnVisibility, validIds),
    },
    columnOrder: applyPinnedLeft(
      validateColumnOrder(loaded.columnOrder, validIds, base.columnOrder),
      columns,
    ),
    columnSizing: {
      ...base.columnSizing,
      ...filterRecord(loaded.columnSizing, validIds),
    },
    visibleFilterIds: (loaded.visibleFilterIds ?? base.visibleFilterIds).filter((id) =>
      validIds.has(id),
    ),
    search: loaded.search ?? base.search,
  }
}

export function loadTablePreferences<TRow>(
  userId: string,
  tableId: string,
  columns: ColumnDefinition<TRow>[],
  defaults?: Partial<Omit<TablePreferencesV1, 'version'>>,
): TablePreferencesV1 {
  if (typeof window === 'undefined' || !isPersistableUserId(userId)) {
    return getDefaultTablePreferences(columns, defaults)
  }
  try {
    const entry = getBrowserStore()[userId]?.[tableId]
    if (!entry?.prefs) return getDefaultTablePreferences(columns, defaults)
    return mergeTablePreferences(columns, entry.prefs, defaults)
  } catch {
    return getDefaultTablePreferences(columns, defaults)
  }
}

export function saveTablePreferences(
  userId: string,
  tableId: string,
  prefs: TablePreferencesV1,
): void {
  if (typeof window === 'undefined' || !isPersistableUserId(userId) || !tableId) return
  mutateUserStore(userId, (s) => ({
    ...s,
    [tableId]: { ...s[tableId], prefs },
  }))
}

// ─── Saved Views local meta ──────────────────────────────────────────────────

export function loadSelectedSavedViewId(
  userId: string,
  listId: string,
): string | null {
  if (!isPersistableUserId(userId) || !listId || isResourceMetaListId(listId)) {
    return null
  }
  const id = getBrowserStore()[userId]?.[listId]?.savedViews?.selectedId
  return typeof id === 'string' && id.length > 0 ? id : null
}

export function saveSelectedSavedViewId(
  userId: string,
  listId: string,
  selectedId: string | null,
): void {
  if (!isPersistableUserId(userId) || !listId || isResourceMetaListId(listId)) return
  mutateUserStore(userId, (s) => {
    const prev = s[listId] ?? {}
    const savedViews = {
      ...prev.savedViews,
      selectedId,
    }
    if (selectedId == null) {
      const { selectedId: _drop, ...rest } = savedViews
      const nextSaved =
        Object.keys(rest).length > 0 ||
        (Array.isArray(rest.includedSharedIds) && rest.includedSharedIds.length > 0)
          ? { ...rest, selectedId: null }
          : { selectedId: null }
      return {
        ...s,
        [listId]: { ...prev, savedViews: nextSaved },
      }
    }
    return {
      ...s,
      [listId]: { ...prev, savedViews },
    }
  })
}

export function loadIncludedSharedViewIds(
  userId: string,
  resourceType: string,
): string[] {
  if (!isPersistableUserId(userId) || !resourceType.trim()) return []
  const key = resourceMetaListId(resourceType.trim())
  const ids = getBrowserStore()[userId]?.[key]?.savedViews?.includedSharedIds
  return Array.isArray(ids) ? ids.filter((id) => typeof id === 'string' && id.length > 0) : []
}

export function saveIncludedSharedViewIds(
  userId: string,
  resourceType: string,
  includedSharedIds: string[],
): void {
  if (!isPersistableUserId(userId) || !resourceType.trim()) return
  const key = resourceMetaListId(resourceType.trim())
  const cleaned = [...new Set(includedSharedIds.filter((id) => typeof id === 'string' && id.length > 0))]
  mutateUserStore(userId, (s) => {
    if (cleaned.length === 0) {
      const prev = s[key]
      if (!prev) return s
      const next = { ...s }
      delete next[key]
      return next
    }
    return {
      ...s,
      [key]: {
        savedViews: { includedSharedIds: cleaned },
      },
    }
  })
}
